import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { camelCase } from 'lodash';

import { FacilityFormType, InstallationFlowType, TechnicianFormType } from '../../constants/app.constants';
import { FormTypesData, KeyValueData } from '../../domains/common.domain';
import { CommonService } from '../../services/common.service';
import { CommunicationService } from '../../services/communication.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { ErrorService } from '../../services/error/error.service';
import { ValidationsService } from '../../services/validations.service';
import { AllDropdownKeys, UrlSegments } from '../jurisdiction.constants';
import {
  CreateGridCompFeedInApplicationResponse,
  CreateInstallationApplicationResponse,
  FeedInInstallationDataObject,
  LookupOptionsData,
} from '../jurisdiction.domain';
import { JurisdictionService } from '../jurisdiction.service';
import { NavigationStateShareService } from '../../services/navigation-state-share.service';
import { GetGridCompFeedInApplicationObject, GetInstallationApplicationObject } from '../facilities-form/facilities-form.domain';
import {
  CreateGridConnectionRequest,
  CreateGridConnectionResponse,
  FormSubsectionsVisibility,
  GetGridConnectionObject,
} from '../technician-data/technician-data.domain';
import { TechnicianResponseData } from '../../employee-overview/employee-overview.domain';
import { Observable } from 'rxjs';
import { EnvironmentService } from '../../services/environment.service';

@Component({
  providers: [ConfirmationService, ValidationsService],
  selector: 'app-media-selection-form',
  templateUrl: './media-selection-form.component.html',
  styleUrls: ['./media-selection-form.component.scss'],
})
export class MediaSelectionFormComponent implements OnInit, AfterViewChecked {
  @ViewChildren('checkboxElement') spanElements: QueryList<ElementRef>;

  buildingTypeKey: string;
  feedInTypeKey: string;
  buildingTypes: LookupOptionsData[];
  feedInTypes: LookupOptionsData[];
  changeRequestTypeKey: string;
  changeRequestTypes: LookupOptionsData[];
  currentInstallationFlow: string;
  devAreaKey: string;
  devAreaTypes: LookupOptionsData[];
  disableFormButtons: boolean;
  installationFlowType: KeyValueData;
  isLoading: boolean;
  isNewInstallation: boolean;
  mediumVisibility: FormSubsectionsVisibility;
  mediaSelectionDataFG: FormGroup;
  options: any;
  selectedBuildingType: string;
  selectedChangeType: string;
  selectedFacilityTypes: FormTypesData[];
  showAreaName: boolean;
  showDropdowns: boolean;
  isFeedSystem: boolean;
  isChangeConnection: boolean;
  isConversionToGas: boolean;
  isDistrictRequired: boolean;
  isGridCompatibility: boolean;
  technicianId: string;
  gasInstallationChangeTypesKey: string;
  gasInstallationChangeTypes: LookupOptionsData[];
  heatingInstallationChangeTypesKey: string;
  heatingInstallationChangeTypes: LookupOptionsData[];
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationChangeTypesKey: string;
  installationChangeTypes: LookupOptionsData[];
  waterInstallationChangeTypesKey: string;
  waterInstallationChangeTypes: LookupOptionsData[];
  reverseBuildingTypes: boolean;
  private mediaTypeUpdated: boolean;

  constructor(
    private confirmationService: ConfirmationService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private jurisdictionService: JurisdictionService,
    private stateShare: NavigationStateShareService,
    private communicationService: CommunicationService,
    private errorService: ErrorService,
    private commonService: CommonService,
    private cdr: ChangeDetectorRef,
    private environmentService: EnvironmentService
  ) {
    this.localStorageService.removeItem('connection_object_id');
    this.localStorageService.removeItem('business_partner_id');
    this.localStorageService.removeItem('gas_installation_id');
    this.localStorageService.removeItem('heating_installation_id');
    this.localStorageService.removeItem('power_installation_id');
    this.localStorageService.removeItem('water_installation_id');
    this.localStorageService.removeItem('feedin_inst_type');
    this.localStorageService.removeItem('installation_id');
    this.localStorageService.removeItem('constructor_id');
    this.localStorageService.removeItem('installaer_id');
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.mediaTypeUpdated = false;
    this.installationFlowType = InstallationFlowType;
    this.selectedBuildingType = '';
    this.selectedChangeType = '';
    this.selectedFacilityTypes = [];
    this.isDistrictRequired = this.environmentService.districtRequired || false;
    this.reverseBuildingTypes = this.environmentService.reverseBuildingTypes;
    this.infoBubblesData = this.environmentService.getInfoBubblesData('location') || null;

    this.buildingTypeKey = AllDropdownKeys.BuildingType;
    this.changeRequestTypeKey = AllDropdownKeys.ChangeSubtype;
    this.devAreaKey = AllDropdownKeys.DevelopmentAreaName;
    this.feedInTypeKey = AllDropdownKeys.FeedInType;
    this.buildingTypes = this.commonService.getDropdownData(this.buildingTypeKey);
    this.changeRequestTypes = this.commonService.getDropdownData(this.changeRequestTypeKey);
    this.devAreaTypes = this.commonService.getDropdownData(this.devAreaKey);
    this.feedInTypes = this.commonService.getDropdownData(this.feedInTypeKey);

    this.installationChangeTypesKey = AllDropdownKeys.InstallationChangeType;
    this.gasInstallationChangeTypesKey = AllDropdownKeys.InstallationChangeTypeGas;
    this.heatingInstallationChangeTypesKey = AllDropdownKeys.InstallationChangeTypeHeating;
    this.waterInstallationChangeTypesKey = AllDropdownKeys.InstallationChangeTypeWater;
    this.installationChangeTypes = this.commonService.getDropdownData(this.installationChangeTypesKey);
    this.gasInstallationChangeTypes = this.commonService.getDropdownData(this.gasInstallationChangeTypesKey);
    this.heatingInstallationChangeTypes = this.commonService.getDropdownData(this.heatingInstallationChangeTypesKey);
    this.waterInstallationChangeTypes = this.commonService.getDropdownData(this.waterInstallationChangeTypesKey);

    this.disableFormButtons = true;
    this.options = this.environmentService.mapData.options;
    this.showAreaName = false;

    this.currentInstallationFlow = this.localStorageService.getItem('installation_flow') || '';
    this.technicianId = this.localStorageService.getItem('technician_id');

    this.isNewInstallation = this.currentInstallationFlow === InstallationFlowType.NewInstallation;
    this.showDropdowns = this.currentInstallationFlow === InstallationFlowType.UpdateInstallation;
    this.isFeedSystem = this.currentInstallationFlow === InstallationFlowType.FeedInSystem;
    this.isChangeConnection = this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection;
    this.isConversionToGas = this.currentInstallationFlow === InstallationFlowType.ConversionToGas;
    this.isGridCompatibility = this.currentInstallationFlow === InstallationFlowType.GridCompatibility;

    this.mediumVisibility = {
      gas: false,
      heating: false,
      power: false,
      water: false,
    };
    this.commonService.installationApplicationData = null;
    this.commonService.locationData = this.localStorageService.getItem('location_data') || null;
    this.prepareForm();
    this.getTechnicianData();
  }

  ngAfterViewChecked(): void {
    if (this.spanElements && this.spanElements.length && !this.mediaTypeUpdated) {
      this.selectMeter();
    }
  }

  getData(): void {
    this.prepareForm();
  }

  selectMeter(event?: any, fieldName: string = null) {
    if (!event && !fieldName) {
      this.spanElements.forEach(element => {
        const iconElement = element.nativeElement.parentElement.children[0];
        const labelElement = element.nativeElement.parentElement.children[1];
        const field = element.nativeElement.id;

        if (this.mediaSelectionDataFG.value[field]) {
          iconElement.classList.add('active-icon');
          labelElement.classList.add('active-icon-label');
        } else {
          iconElement.classList.remove('active-icon');
          labelElement.classList.remove('active-icon-label');
        }
      });
      this.mediaTypeUpdated = true;
    } else {
      const element = (event.target && event.target.parentNode) || null;

      if (element) {
        const iconElement = element.children[0];
        const labelElement = element.children[1];

        if (!iconElement.classList.contains('active-icon')) {
          iconElement.classList.add('active-icon');
          labelElement.classList.add('active-icon-label');

          this.mediaSelectionDataFG.controls[fieldName].setValue(true);
          if (this.currentInstallationFlow === this.installationFlowType.UpdateInstallation) {
            this.mediaSelectionDataFG.controls[`${fieldName}Type`].setValidators([Validators.required]);
            this.mediaSelectionDataFG.controls[`${fieldName}Type`].markAsTouched();
            this.mediaSelectionDataFG.controls[`${fieldName}Type`].updateValueAndValidity();
          }
        } else {
          iconElement.classList.remove('active-icon');
          labelElement.classList.remove('active-icon-label');

          this.mediaSelectionDataFG.controls[fieldName].setValue(false);
          if (this.currentInstallationFlow === this.installationFlowType.UpdateInstallation) {
            this.mediaSelectionDataFG.controls[`${fieldName}Type`].reset();
            this.mediaSelectionDataFG.controls[`${fieldName}Type`].clearValidators();
            this.mediaSelectionDataFG.controls[`${fieldName}Type`].updateValueAndValidity();
          }
        }
      }
    }
  }

  evaluateFormValidity() {
    if (this.isFeedSystem) {
      this.disableFormButtons = this.mediaSelectionDataFG.invalid;
    } else if (this.isChangeConnection || this.isConversionToGas) {
      this.disableFormButtons = !this.selectedChangeType.length || this.mediaSelectionDataFG.invalid;
    } else if (this.currentInstallationFlow === this.installationFlowType.MainsConnection) {
      this.disableFormButtons =
        this.mediaSelectionDataFG.invalid ||
        !this.selectedBuildingType.length ||
        !(
          this.mediaSelectionDataFG.valid &&
          (this.mediaSelectionDataFG.value.power ||
            this.mediaSelectionDataFG.value.gas ||
            this.mediaSelectionDataFG.value.water ||
            this.mediaSelectionDataFG.value.heating)
        );
    } else {
      this.disableFormButtons =
        this.mediaSelectionDataFG.invalid ||
        !(
          this.mediaSelectionDataFG.valid &&
          (this.mediaSelectionDataFG.value.power ||
            this.mediaSelectionDataFG.value.gas ||
            this.mediaSelectionDataFG.value.water ||
            this.mediaSelectionDataFG.value.heating)
        );
    }
    this.cdr.detectChanges();
  }

  displayAreaName(event: boolean): void {
    this.mediaSelectionDataFG.controls.devAreaName.reset();
    this.showAreaName = event;
    if (this.showAreaName) {
      this.mediaSelectionDataFG.controls.devAreaName.setValidators([Validators.required]);
    } else {
      this.mediaSelectionDataFG.controls.devAreaName.clearValidators();
    }
    this.mediaSelectionDataFG.controls.devAreaName.updateValueAndValidity();
    this.evaluateFormValidity();
  }

  submitFormData(): void {
    this.checkForExistingApplication();
  }

  setFeedInInstType(type: string) {
    this.localStorageService.setItem('feedin_inst_type', type);
  }

  private prepareForm(): void {
    this.mediaSelectionDataFG = new FormGroup({
      postcode: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.postcode) || '',
          disabled: false,
        },
        [Validators.required, ValidationsService.maxLengthValidator()]
      ),
      city: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.city) || '',
          disabled: false,
        },
        Validators.required
      ),
      street: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.street) || '',
          disabled: false,
        },
        Validators.required
      ),
      street_number: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.street_number) || '',
          disabled: false,
        },
        [Validators.required, ValidationsService.streetNumberValidator()]
      ),
      power: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.power) || false,
          disabled: false,
        },
        [Validators.required]
      ),
      gas: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.gas) || false,
          disabled: false,
        },
        [Validators.required]
      ),
      water: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.water) || false,
          disabled: false,
        },
        [Validators.required]
      ),
      heating: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.heating) || false,
          disabled: false,
        },
        [Validators.required]
      ),
    });

    if (this.environmentService.showDistrict) {
      this.mediaSelectionDataFG.addControl(
        'district',
        new FormControl({
          value: (this.commonService.locationData && this.commonService.locationData.district) || '',
          disabled: false,
        })
      );
      if (this.isDistrictRequired) {
        this.mediaSelectionDataFG.controls.district.setValidators(Validators.required);
        this.mediaSelectionDataFG.controls.district.updateValueAndValidity();
      }
    }

    if (this.currentInstallationFlow === this.installationFlowType.UpdateInstallation) {
      this.mediaSelectionDataFG.addControl('powerType', new FormControl({ value: '', disabled: false }));
      this.mediaSelectionDataFG.addControl('gasType', new FormControl({ value: '', disabled: false }));
      this.mediaSelectionDataFG.addControl('waterType', new FormControl({ value: '', disabled: false }));
      this.mediaSelectionDataFG.addControl('heatingType', new FormControl({ value: '', disabled: false }));
    } else if (this.isFeedSystem) {
      this.mediaSelectionDataFG.removeControl('power');
      this.mediaSelectionDataFG.removeControl('gas');
      this.mediaSelectionDataFG.removeControl('water');
      this.mediaSelectionDataFG.removeControl('heating');
      this.mediaSelectionDataFG.addControl('construction-type', new FormControl('', [Validators.required]));
    } else if (this.currentInstallationFlow === this.installationFlowType.TemporaryConnection) {
      this.mediaSelectionDataFG.addControl('newDevArea', new FormControl({ value: false, disabled: false }));
      this.mediaSelectionDataFG.addControl('devAreaName', new FormControl({ value: '', disabled: false }));
      this.mediaSelectionDataFG.removeControl('gas');
      this.mediaSelectionDataFG.removeControl('heating');
    } else if (this.isChangeConnection) {
      this.mediaSelectionDataFG.removeControl('power');
      this.mediaSelectionDataFG.removeControl('gas');
      this.mediaSelectionDataFG.removeControl('water');
      this.mediaSelectionDataFG.removeControl('heating');
      this.mediaSelectionDataFG.addControl('connection-modification', new FormControl('', [Validators.required]));
    } else if (this.isConversionToGas) {
      this.mediaSelectionDataFG.removeControl('power');
      this.mediaSelectionDataFG.removeControl('gas');
      this.mediaSelectionDataFG.removeControl('water');
      this.mediaSelectionDataFG.removeControl('heating');
      this.mediaSelectionDataFG.addControl('conversionTypeGas', new FormControl(''));
      this.mediaSelectionDataFG.addControl('conversionTypeHeat', new FormControl(''));
    } else if (this.isGridCompatibility) {
      this.mediaSelectionDataFG.removeControl('power');
      this.mediaSelectionDataFG.removeControl('gas');
      this.mediaSelectionDataFG.removeControl('water');
      this.mediaSelectionDataFG.removeControl('heating');
      this.mediaSelectionDataFG.addControl('area', new FormControl(''));
      this.mediaSelectionDataFG.addControl('field', new FormControl(''));
      this.mediaSelectionDataFG.addControl('field-sector', new FormControl(''));
    }

    if (this.currentInstallationFlow === this.installationFlowType.MainsConnection) {
      this.mediaSelectionDataFG.addControl('newDevArea', new FormControl({ value: false, disabled: false }));
      this.mediaSelectionDataFG.addControl('devAreaName', new FormControl({ value: '', disabled: false }));
      this.mediaSelectionDataFG.addControl('business', new FormControl({ value: false, disabled: false }));
      this.mediaSelectionDataFG.addControl('singleFamily', new FormControl({ value: false, disabled: false }));
      this.mediaSelectionDataFG.addControl('multiFamily', new FormControl({ value: false, disabled: false }));
      this.mediaSelectionDataFG.addControl('apartmentHouse', new FormControl({ value: false, disabled: false }));
    }

    this.evaluateFormValidity();
    this.mediaSelectionDataFG.valueChanges.subscribe(() => {
      this.evaluateFormValidity();
    });
  }

  private checkForExistingApplication(): void {
    let applicationSubscription: Observable<any>;
    let flowType: number;
    if (
      this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
      this.currentInstallationFlow === this.installationFlowType.UpdateInstallation ||
      this.currentInstallationFlow === this.installationFlowType.FeedInSystem
    ) {
      flowType = 1;
      applicationSubscription = this.commonService.getExistingInstallationApplications(this.currentInstallationFlow);
    } else if (
      this.currentInstallationFlow === this.installationFlowType.MainsConnection ||
      this.currentInstallationFlow === this.installationFlowType.TemporaryConnection ||
      this.currentInstallationFlow === this.installationFlowType.ChangeHouseConnection ||
      this.currentInstallationFlow === this.installationFlowType.ConversionToGas
    ) {
      flowType = 2;
      applicationSubscription = this.commonService.getExistingGridConnectionApplication(this.currentInstallationFlow);
    } else if (this.currentInstallationFlow === this.installationFlowType.GridCompatibility) {
      flowType = 3;
      applicationSubscription = this.commonService.getExistingGridCompFeedInApplications(this.currentInstallationFlow);
    }

    this.isLoading = true;
    applicationSubscription.subscribe(
      (response: GetInstallationApplicationObject | GetGridConnectionObject | GetGridCompFeedInApplicationObject) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        if (
          response &&
          (((response as GetInstallationApplicationObject).installationapplication &&
            (response as GetInstallationApplicationObject).installationapplication.length) ||
            (response as GetGridCompFeedInApplicationObject)?.application?.length ||
            ((response as GetGridConnectionObject).gridconapplication && (response as GetGridConnectionObject).gridconapplication.length))
        ) {
          this.confirmationService.confirm({
            accept: () => {
              this.createNewApplication();
            },
            reject: () => {
              this.setSelectedFacilities();
              if (this.selectedFacilityTypes.length) {
                this.resetExistingApplication(response as GetInstallationApplicationObject | GetGridConnectionObject);
              } else {
                if (flowType === 1) {
                  this.commonService.installationApplicationData = (
                    response as GetInstallationApplicationObject
                  ).installationapplication[0];
                } else if (flowType === 2) {
                  this.commonService.gridConnectionApplicationData = (response as GetGridConnectionObject).gridconapplication[0];
                } else if (flowType === 3) {
                  this.commonService.gridCompFeedInApplicationData = (response as GetGridCompFeedInApplicationObject).application[0];
                }
                this.handleDataOnRedirection(response);
              }
            },
          });
        } else {
          this.createNewApplication();
        }
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private createNewApplication(): void {
    if (
      this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
      this.currentInstallationFlow === this.installationFlowType.UpdateInstallation ||
      this.currentInstallationFlow === this.installationFlowType.FeedInSystem
    ) {
      this.createInstallationApplication();
    } else if (
      this.currentInstallationFlow === this.installationFlowType.MainsConnection ||
      this.currentInstallationFlow === this.installationFlowType.TemporaryConnection ||
      this.currentInstallationFlow === this.installationFlowType.ChangeHouseConnection ||
      this.currentInstallationFlow === this.installationFlowType.ConversionToGas
    ) {
      this.createGridConnectionApplication();
    } else if (this.currentInstallationFlow === this.installationFlowType.GridCompatibility) {
      this.createGridCompFeedInApplication();
    }
  }

  private createInstallationApplication(): void {
    this.isLoading = true;
    this.commonService.createInstallationApplication(this.currentInstallationFlow, null).subscribe(
      (data: CreateInstallationApplicationResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(data, true);
        this.commonService.installationApplicationData = null;
        this.handleDataOnRedirection(data);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private createGridCompFeedInApplication(): void {
    this.isLoading = true;
    this.commonService.createGridCompFeedInApplication(this.currentInstallationFlow).subscribe(
      (data: CreateGridCompFeedInApplicationResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(data, true);
        this.commonService.installationApplicationData = null;
        this.handleDataOnRedirection(data);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private createGridConnectionApplication(): void {
    this.isLoading = true;
    const requestData: CreateGridConnectionRequest = {
      sk_gridconapplication_type: this.commonService.getGridConnectionApplicationType(this.currentInstallationFlow),
      change_power: this.mediaSelectionDataFG.value.power ? '1' : '0',
      change_gas: this.mediaSelectionDataFG.value.gas || this.selectedChangeType === 'conversion_gas' ? '1' : '0',
      change_water: this.mediaSelectionDataFG.value.water ? '1' : '0',
      change_heat: this.mediaSelectionDataFG.value.heating || this.selectedChangeType === 'conversion_heat' ? '1' : '0',
      automatic_offer: this.environmentService.createAutomaticOffer ? '1' : '0',
    };

    if (this.currentInstallationFlow === InstallationFlowType.MainsConnection) {
      requestData.sk_building_type = this.selectedBuildingType;
      requestData.development_area = this.mediaSelectionDataFG.value.newDevArea ? '1' : '0';
    } else if (
      this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection ||
      this.currentInstallationFlow === InstallationFlowType.ConversionToGas
    ) {
      requestData.sk_change_subtype = this.selectedChangeType;
    }

    this.commonService.createGridConnectionApplication(requestData).subscribe(
      (data: CreateGridConnectionResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(data, true);
        this.commonService.gridConnectionApplicationData = null;
        this.handleDataOnRedirection(data);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private handleDataOnRedirection(
    data:
      | CreateInstallationApplicationResponse
      | CreateGridConnectionResponse
      | CreateGridCompFeedInApplicationResponse
      | GetGridCompFeedInApplicationObject
      | GetGridConnectionObject
      | GetInstallationApplicationObject = null
  ): void {
    this.setSelectedFacilities();
    if (this.selectedFacilityTypes.length) {
      this.localStorageService.setItem('selected_facilities', this.selectedFacilityTypes);
    }
    if (this.currentInstallationFlow === InstallationFlowType.MainsConnection) {
      const selectedBuildingType = TechnicianFormType.filter(item => item.lookUpKey === this.selectedBuildingType);
      this.localStorageService.setItem('selected_building_type', selectedBuildingType);
    }
    if (
      this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection ||
      this.currentInstallationFlow === InstallationFlowType.ConversionToGas
    ) {
      this.localStorageService.setItem('selected_change_type', this.selectedChangeType);
    }
    this.localStorageService.setItem('location_data', this.mediaSelectionDataFG.value);
    this.commonService.locationData = this.mediaSelectionDataFG.value;

    if (data) {
      const applicationId =
        ((data as CreateInstallationApplicationResponse).installationapplication &&
          (data as CreateInstallationApplicationResponse).installationapplication[0] &&
          (data as CreateInstallationApplicationResponse).installationapplication[0].application_id) ||
        ((data as CreateGridConnectionResponse).gridconapplication &&
          (data as CreateGridConnectionResponse).gridconapplication[0] &&
          (data as CreateGridConnectionResponse).gridconapplication[0].application_id) ||
        (data as CreateGridCompFeedInApplicationResponse)?.application[0]?.application_id;

      this.localStorageService.setItem('application_id', applicationId);
    }
    if (this.currentInstallationFlow === InstallationFlowType.FeedInSystem) {
      const feedInInstallationData: FeedInInstallationDataObject = (data as GetInstallationApplicationObject).installationapplication[0]
        .feedininstallation;
      if (feedInInstallationData && feedInInstallationData.installation_id) {
        this.localStorageService.setItem('installation_id', feedInInstallationData.installation_id);
        this.localStorageService.setItem('constructor_id', feedInInstallationData.constructor.constructor_id);
        this.localStorageService.setItem('installer_id', feedInInstallationData.installer.installer_id);
      }
    }

    if (
      this.currentInstallationFlow === InstallationFlowType.MainsConnection ||
      this.currentInstallationFlow === InstallationFlowType.TemporaryConnection ||
      this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection ||
      this.currentInstallationFlow === InstallationFlowType.ConversionToGas
    ) {
      this.router.navigate(['/technician-form']);
    } else if (this.currentInstallationFlow === InstallationFlowType.FeedInSystem) {
      this.router.navigate(['/technical-data']);
    } else {
      if (this.environmentService.showTechnicalFormFirst) {
        this.router.navigate(['/facilities-data']);
      } else {
        this.router.navigate(['/personal-data']);
      }
    }
    this.stateShare.setFormState(2);
  }

  private getTechnicianData(): void {
    this.isLoading = true;
    this.communicationService.getData(`technician?technician_id=${this.technicianId}`).subscribe(
      (response: TechnicianResponseData) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        const technicianData = (response.technician && response.technician[0]) || null;
        this.commonService.technicianData = technicianData;
        this.mediumVisibility = this.commonService.getPermittedTradesData(technicianData);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private resetExistingApplication(applicationData: GetInstallationApplicationObject | GetGridConnectionObject): void {
    const isInstallationFlow =
      this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
      this.currentInstallationFlow === this.installationFlowType.UpdateInstallation ||
      this.currentInstallationFlow === this.installationFlowType.FeedInSystem;
    const applicationId = isInstallationFlow
      ? (applicationData as GetInstallationApplicationObject).installationapplication[0].application_id
      : (applicationData as GetGridConnectionObject).gridconapplication[0].application_id;
    if (this.selectedFacilityTypes.length) {
      if (!isInstallationFlow && applicationId) {
        const requestData = {
          change_gas: '0',
          change_heat: '0',
          change_power: '0',
          change_water: '0',
        };
        FacilityFormType.forEach(item => {
          const selectedFacility = this.selectedFacilityTypes.filter(type => type.key === item.key);
          if (selectedFacility.length) {
            switch (item.key) {
              case 'gas':
                requestData.change_gas = '1';
                break;
              case 'heating':
                requestData.change_heat = '1';
                break;
              case 'power':
                requestData.change_power = '1';
                break;
              case 'water':
                requestData.change_water = '1';
                break;
            }
          }
        });
        this.isLoading = true;
        this.commonService.updateGridConnectionApplication(applicationId, requestData).subscribe(
          updateApplicationResponse => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(updateApplicationResponse);
            this.resetApplicationSubsections(isInstallationFlow, applicationData);
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        this.resetApplicationSubsections(isInstallationFlow, applicationData);
      }
    }
  }

  private resetApplicationSubsections(
    isInstallationFlow: boolean,
    applicationData: GetInstallationApplicationObject | GetGridConnectionObject
  ): void {
    let subscriptionCount = -1;
    FacilityFormType.forEach(item => {
      const selectedFacility = this.selectedFacilityTypes.filter(type => type.key === item.key);
      let installationId = '';
      let urlSegment = '';

      switch (item.key) {
        case 'power':
          // if (!selectedFacility.length) {
          installationId = isInstallationFlow
            ? (applicationData as GetInstallationApplicationObject).installationapplication[0].powerinstallation &&
              (applicationData as GetInstallationApplicationObject).installationapplication[0].powerinstallation.installation_id
            : (applicationData as GetGridConnectionObject).gridconapplication[0].powergridcon &&
              (applicationData as GetGridConnectionObject).gridconapplication[0].powergridcon.gridcon_id;
          urlSegment = isInstallationFlow
            ? `${UrlSegments.UpdatePowerInstallation}=${installationId}`
            : `${UrlSegments.UpdatePowerGridConnection}${installationId}`;
          // }
          break;
        case 'gas':
          // if (!selectedFacility.length) {
          installationId = isInstallationFlow
            ? (applicationData as GetInstallationApplicationObject).installationapplication[0].gasinstallation &&
              (applicationData as GetInstallationApplicationObject).installationapplication[0].gasinstallation.installation_id
            : (applicationData as GetGridConnectionObject).gridconapplication[0].gasgridcon &&
              (applicationData as GetGridConnectionObject).gridconapplication[0].gasgridcon.gridcon_id;
          urlSegment = isInstallationFlow
            ? `${UrlSegments.UpdateGasInstallation}=${installationId}`
            : `${UrlSegments.UpdateGasGridConnection}${installationId}`;
          // }
          break;
        case 'water':
          // if (!selectedFacility.length) {
          installationId = isInstallationFlow
            ? (applicationData as GetInstallationApplicationObject).installationapplication[0].waterinstallation &&
              (applicationData as GetInstallationApplicationObject).installationapplication[0].waterinstallation.installation_id
            : (applicationData as GetGridConnectionObject).gridconapplication[0].watergridcon &&
              (applicationData as GetGridConnectionObject).gridconapplication[0].watergridcon.gridcon_id;
          urlSegment = isInstallationFlow
            ? `${UrlSegments.UpdateWaterInstallation}=${installationId}`
            : `${UrlSegments.UpdateWaterGridConnection}${installationId}`;
          // }
          break;
        case 'heating':
          // if (!selectedFacility.length) {
          installationId = isInstallationFlow
            ? (applicationData as GetInstallationApplicationObject).installationapplication[0].districtheatinstallation &&
              (applicationData as GetInstallationApplicationObject).installationapplication[0].districtheatinstallation.installation_id
            : (applicationData as GetGridConnectionObject).gridconapplication[0].districtheatgridcon &&
              (applicationData as GetGridConnectionObject).gridconapplication[0].districtheatgridcon.gridcon_id;
          urlSegment = isInstallationFlow
            ? `${UrlSegments.UpdateHeatingInstallation}=${installationId}`
            : `${UrlSegments.UpdateHeatingGridConnection}${installationId}`;
          // }
          break;
      }
      if (installationId) {
        if (!selectedFacility.length) {
          subscriptionCount = subscriptionCount === -1 ? 1 : subscriptionCount + 1;
          this.isLoading = true;
          this.commonService.deleteMediaObjectsFromApplication(urlSegment).subscribe(
            applicationResponse => {
              this.isLoading = false;
              subscriptionCount -= 1;
              this.errorService.throwErrorIfFailure(applicationResponse, true);

              if (subscriptionCount === 0) {
                this.isLoading = true;
                const subscription: Observable<GetInstallationApplicationObject | GetGridConnectionObject> = isInstallationFlow
                  ? this.commonService.getExistingInstallationApplications(this.currentInstallationFlow)
                  : this.commonService.getExistingGridConnectionApplication(this.currentInstallationFlow);

                subscription.subscribe(
                  (response: GetInstallationApplicationObject | GetGridConnectionObject) => {
                    this.isLoading = false;
                    this.errorService.throwErrorIfFailure(response, true);
                    if (
                      response &&
                      (response as GetInstallationApplicationObject).installationapplication &&
                      (response as GetInstallationApplicationObject).installationapplication.length
                    ) {
                      this.commonService.installationApplicationData = (
                        response as GetInstallationApplicationObject
                      ).installationapplication[0];
                    } else if (
                      response &&
                      (response as GetGridConnectionObject).gridconapplication &&
                      (response as GetGridConnectionObject).gridconapplication.length
                    ) {
                      this.commonService.gridConnectionApplicationData = (response as GetGridConnectionObject).gridconapplication[0];
                    }
                    this.handleDataOnRedirection(response);
                  },
                  error => {
                    this.isLoading = false;
                    this.errorService.throwErrorIfFailure(error, true);
                  }
                );
              }
            },
            error => {
              this.isLoading = false;
              this.errorService.throwErrorIfFailure(error, true);
            }
          );
        } else {
          let requestObject = {};
          this.isLoading = true;
          if (this.currentInstallationFlow === this.installationFlowType.UpdateInstallation) {
            if (item.key === 'power') {
              requestObject = {
                sk_inst_change_type: this.mediaSelectionDataFG.value.powerType,
              };
            } else if (item.key === 'gas') {
              requestObject = {
                sk_inst_change_type_gas: this.mediaSelectionDataFG.value.gasType,
              };
            } else if (item.key === 'water') {
              requestObject = {
                sk_inst_change_type_water: this.mediaSelectionDataFG.value.waterType,
              };
            } else if (item.key === 'heating') {
              requestObject = {
                sk_inst_change_type_districtheat: this.mediaSelectionDataFG.value.heatingType,
              };
            }
            this.commonService.updateMediaObjectsFromApplication(urlSegment, requestObject).subscribe(
              response => {
                this.isLoading = true;
                this.errorService.throwErrorIfFailure(response, true);
                this.commonService.getExistingInstallationApplications(this.currentInstallationFlow).subscribe(
                  applicationResponse => {
                    this.isLoading = false;
                    if (
                      applicationResponse &&
                      (applicationResponse as GetInstallationApplicationObject).installationapplication &&
                      (applicationResponse as GetInstallationApplicationObject).installationapplication.length
                    ) {
                      this.commonService.installationApplicationData = (
                        applicationResponse as GetInstallationApplicationObject
                      ).installationapplication[0];
                    }
                    this.handleDataOnRedirection(applicationResponse);
                  },
                  error => {
                    this.isLoading = false;
                    this.errorService.throwErrorIfFailure(error, true);
                  }
                );
              },
              error => {
                this.isLoading = false;
                this.errorService.throwErrorIfFailure(error, true);
              }
            );
          }
        }
      }
    });
    if (subscriptionCount === -1) {
      if (isInstallationFlow) {
        this.commonService.installationApplicationData = (applicationData as GetInstallationApplicationObject).installationapplication[0];
      } else {
        this.commonService.gridConnectionApplicationData = (applicationData as GetGridConnectionObject).gridconapplication[0];
      }
      this.handleDataOnRedirection(applicationData);
    }
  }

  private setSelectedFacilities(): void {
    this.selectedFacilityTypes = [];
    FacilityFormType.forEach(item => {
      if (this.mediaSelectionDataFG.value[camelCase(item.key)]) {
        this.selectedFacilityTypes.push(item);
      }
    });

    if (this.selectedFacilityTypes.length === 0) {
      if (this.currentInstallationFlow === this.installationFlowType.ConversionToGas) {
        this.selectedFacilityTypes =
          FacilityFormType.filter(item => {
            if (
              (this.selectedChangeType === 'conversion_gas' && item.key === 'gas') ||
              (this.selectedChangeType === 'conversion_heat' && item.key === 'heating')
            ) {
              return item;
            }
          }) || [];
      }
    }
  }
}
